import type { IAttributes, INumberAttribute, ITextAttribute } from './types/listings-api/Products';

/**
 * The attribute keys the recommendations endpoint is contracted to return and
 * that consumers read by name (DISC-4524).
 *
 * `IProductBasic.attributes` stays an open passthrough of every Vertex custom
 * attribute, including commercially sensitive ones such as `actual_comms_rate`,
 * so it must never be spread onto anything a customer or a page payload can see.
 * Reading named keys is the only safe way to use it, and these are the named
 * keys. Every one is optional, matching listings-api's own schema, where a
 * required attribute a product does not carry would drop the product from the
 * carousel rather than merely lose it a badge.
 */
export interface IRecommendationsAttributes {
  /**
   * Delivery cost in **major units**: `3.99` means £3.99, unlike `price.amount`
   * and `preSalePrices`, which the same response quotes in minor units.
   *
   * It is the **cheapest** of the product's per-type costs, and it describes UK
   * mainland delivery, so it means nothing to a customer delivering elsewhere.
   *
   * Nothing reads it. See `delivery_days` for why.
   */
  delivery_cost?: INumberAttribute;
  /**
   * **Not an arrival lead time, and nothing reads it. Do not render a delivery
   * date from this.**
   *
   * Measured across 299 prod products on 3 Sept 2026: this sits *below* the
   * fastest `delivery_days_<TYPE>` value on 220 of them, by 1 to 4 days, and is
   * never slower than one. So it is not an aggregate of them, and no delivery
   * option achieves it. It behaves like dispatch or make time, where the per-type
   * values are make plus transit.
   *
   * A card needs the arrival date, which is what the PLP grid resolves from
   * `delivery_days_FREE`, else `delivery_days_STANDARD`. This endpoint returns
   * neither, so a recommendations-fed carousel has nothing it can honestly date
   * from and shows no delivery line at all. Pairing this with `delivery_cost`
   * quotes the cheapest price against a date the product cannot arrive by: it put
   * the date 1 to 4 days early on 74% of products against the grid's own card for
   * the same product.
   *
   * DISC-4526 is the authority on the semantics.
   */
  delivery_days?: INumberAttribute;
  /**
   * Text `"true"` or `"false"`. Unreliable: products carrying a genuine free
   * option can read `"false"`, and the flag covered far more of the catalogue
   * than the per-type data supported. Prefer a resolved cost of zero.
   */
  delivery_free?: ITextAttribute;
  /** Text `"true"` or `"false"`. Surfaced to customers as the "Most Loved" badge. */
  highly_rated?: ITextAttribute;
  /**
   * Text `"true"` or `"false"`. The Top Brand flag. Named `star_brand` in the
   * catalogue; there is no `top_brand` attribute, so read it with `isTopBrand`
   * rather than by a name that looks right.
   *
   * Surfaced to customers as the "Top Brand" badge, but only where "Most Loved"
   * has not already claimed the card's single badge slot. It is much the commoner
   * of the two: on 598 prod products, 16% carry it against Most Loved's 2%.
   */
  star_brand?: ITextAttribute;
}

const textValue = (
  attributes: IAttributes,
  key: keyof IRecommendationsAttributes,
): string | undefined => (attributes[key] as ITextAttribute | undefined)?.text?.[0];

/**
 * Whether the product carries the Highly Rated flag, shown as "Most Loved".
 *
 * The cast is the point of this module: `attributes` is typed as an open map of
 * text-or-number attributes, so every consumer would otherwise narrow the same
 * key by hand. A value arriving in the wrong shape reads as absent rather than
 * throwing, since a badge is not worth a broken carousel.
 */
export const isHighlyRated = (attributes: IAttributes): boolean =>
  textValue(attributes, 'highly_rated') === 'true';

/**
 * Whether the product's partner carries the Top Brand flag, shown as "Top Brand".
 *
 * Reads `star_brand`, which is the only name the catalogue uses for it. Same
 * tolerance as `isHighlyRated`: a value in an unexpected shape reads as absent
 * rather than throwing.
 */
export const isTopBrand = (attributes: IAttributes): boolean =>
  textValue(attributes, 'star_brand') === 'true';
